import React from 'react'
import { useTranslation } from 'react-i18next'

import {
  Table,
  TableBody,
  TableHead,
  TableHeadSort,
  TableHeader,
  TableRow,
} from '@ui/v2/Table/Table'

import { PageQuery } from '@utils/ApiClient'
import { getCentralRowIdConditionally } from '@utils/getItemId'

import { TransactionDto } from '@model/TransactionDto'

import {
  TransactionHistoryEmptyState,
  TransactionHistoryRowCells,
} from '@shared/components/TransactionHistoryTable'
import styles from '@shared/components/TransactionHistoryTable/transactionHistoryTable.module.scss'

type Props = {
  data: TransactionDto[]
  pageKey?: React.Key
  isLoading: boolean
  pageQuery?: PageQuery
  activeFilters?: number
  hasSearch?: boolean
  onRowClick: (tx: TransactionDto) => void
  walletId?: string
  setPageQuery: ((pageQuery?: PageQuery | undefined) => void) | undefined
}

export const PaymentAgentTransactionHistoryTable: React.FC<Props> = ({
  pageQuery,
  setPageQuery,
  isLoading,
  data,
  pageKey,
  activeFilters = 0,
  hasSearch = false,
  onRowClick,
  walletId,
}) => {
  const { t } = useTranslation()

  const setSort = (sort?: string | null, sortOrder?: 'ASC' | 'DESC') => {
    if (!setPageQuery) return
    setPageQuery({ ...pageQuery, sort: sort ?? undefined, sortOrder })
  }

  const hasActiveFilters = activeFilters > 0 || hasSearch

  const noResultsNode =
    data.length === 0 ? <TransactionHistoryEmptyState hasActiveFilters={hasActiveFilters} /> : null

  return (
    <Table isLoading={isLoading} noResults={noResultsNode} className={styles.table}>
      <TableHeader>
        <TableRow>
          <TableHeadSort
            id='TransactionType.name'
            align='left'
            width={190}
            className={styles.typeCell}
            sort={pageQuery?.sort}
            sortOrder={pageQuery?.sortOrder}
            setSort={setSort}
          >
            {t('Type')}
          </TableHeadSort>
          <TableHead align='right' width={144} className={styles.amountRow}>
            {t('Amount')}
          </TableHead>
          <TableHead width={32} className={styles.amountSpacerCell} />
          <TableHead align='left' width={180} className={styles.fromToCell}>
            {t('From')}
          </TableHead>
          <TableHead align='center' width={60} className={styles.arrowCell} />
          <TableHead align='left' width={180} className={styles.fromToCell}>
            {t('To')}
          </TableHead>
          <TableHeadSort
            id='TransactionState.name'
            align='left'
            width={154}
            className={styles.statusCell}
            sort={pageQuery?.sort}
            sortOrder={pageQuery?.sortOrder}
            setSort={setSort}
          >
            {t('Status')}
          </TableHeadSort>
          <TableHead align='left' className={styles.nameRow}>
            {t('Wallet.Reference number')}
          </TableHead>
          <TableHead width={40} />
        </TableRow>
      </TableHeader>

      {!!data.length && (
        <TableBody animateNewRows pageKey={pageKey}>
          {data.map((tx, index) => (
            <TableRow
              key={tx.id}
              {...getCentralRowIdConditionally(
                index,
                data.length,
                'payment-agent-transaction-history'
              )}
              className={styles.clickableRow}
              onClick={() => onRowClick(tx)}
            >
              <TransactionHistoryRowCells tx={tx} context='paymentAgent' walletId={walletId} />
            </TableRow>
          ))}
        </TableBody>
      )}
    </Table>
  )
}
